import { mergeLeft } from 'rambda'
import { DeliveryZoneFormState } from './types'
import { DeliveryZone } from '@@partner:types:app'
import { EXPRESS_DELIVERY_PROVIDERS, SLOT_DELIVERY_PROVIDERS } from '@@partner:config/deliveryZone'
import { DeliveryZoneInfoInput } from '@@partner:graphql/mutation/deliveryZone/saveDeliveryZone.gql'
import { mergeLeftDropping } from 'merge-left-utils'

export const DEFAULT_DELIVERY_ZONE_STATE: DeliveryZoneFormState = {
  id: undefined,
  shop_id: undefined,
  name: '',
  status: 0,
  zoneGeoDeliveryZone: [],
  minOrderCost: 0,
  tzOffset: 0,
  
  zoneSlotMaxWeight: 0,
  zoneSlotMaxWeightUnlimited: true,
  zoneDeliveryCost: 0,
  zoneFirstDeliveryCost: 0,
  freeSlotDeliveryCostFrom: 0,

  zoneExpressMaxWeight: 0,
  zoneExpressMaxWeightUnlimited: true,
  zoneExpressDeliveryCost: 0,
  zoneHasExpressDeliveryEnabled: false,
  zoneTsExpressDeliveryStart: 0,
  zoneTsExpressDeliveryFinish: 0,
  expressDeliveryMaxShippingTime: 0,
  freeExpressDeliveryCostFrom: 0,
  useFirstDeliveryPrice: true,

  slotPlans: [],
  slotDeliveryProviders: [],
  expressDeliveryProviders: [],
  customer_order: 0,

  is_serving_retail: true,
  is_serving_wholesale: true,

  slotProvider: 'inhouse',
  slotConfig: {
    max_orders_limit: 50,
    max_order_positions_limit: 500,
    slot_timeout_value: 180000,
    generateFrom: -27000,
    generateTo: 27000
  },
  slotDeliveryPriceProvider: 'inhouse',
  expressDeliveryPriceProvider: 'inhouseExpress',
  expressDeliveryTimeout: 180000,
  startDeliveryOnPlaceOrder: false,


  slotPriceMultuplier: 1,
  slotPriceAdditional: 0,
  expressPriceMultuplier: 1,
  expressPriceAdditional: 0
}



export const makeDeliveryZoneFormState = (deliveryZone?: DeliveryZone): DeliveryZoneFormState => {
  if (deliveryZone) {
    const slotDeliveryProviders = 
      deliveryZone.deliveryProviders
        .filter(
          provider => {
            return SLOT_DELIVERY_PROVIDERS.some(slotP => slotP.value === provider)
          }
        )
    const expressDeliveryProviders =
      deliveryZone.deliveryProviders
        .filter(
          provider => {
            return EXPRESS_DELIVERY_PROVIDERS.some(expressP => expressP.value === provider)
          }
        )

    const slotPlans = deliveryZone.slotPlans.map(slotPlan => ({
      id: slotPlan.id,
      positionsLimit: Number(slotPlan.positionsLimit) || 0,
      ordersLimit: Number(slotPlan.ordersLimit) || 0,
      slotStartTime: slotPlan.slotStartTime,
      slotFinishTime: slotPlan.slotFinishTime,
      weekDay: Number(slotPlan.weekDay),
      slotTimeout: slotPlan.slotTimeout / 60000
    }))

    return mergeLeft<DeliveryZoneFormState>(
      {
        ...deliveryZone,
        slotDeliveryProviders,
        expressDeliveryProviders,
        slotPlans,
        zoneSlotMaxWeight: (deliveryZone.zoneSlotMaxWeight ?? 0) / 1000,
        zoneSlotMaxWeightUnlimited: (deliveryZone.zoneSlotMaxWeight ?? 0) === 0,
        zoneExpressMaxWeight: (deliveryZone.zoneExpressMaxWeight ?? 0) / 1000,
        zoneExpressMaxWeightUnlimited: (deliveryZone.zoneExpressMaxWeight ?? 0) === 0
      }, 
      DEFAULT_DELIVERY_ZONE_STATE
    )
  }
  return DEFAULT_DELIVERY_ZONE_STATE
  
}

export const mapStateToApoVariable = (state: DeliveryZoneFormState): DeliveryZoneInfoInput => {
  const prepared = mergeLeftDropping(
    [
      'slotDeliveryProviders',
      'expressDeliveryProviders',
      'zoneSlotMaxWeightUnlimited',
      'zoneExpressMaxWeightUnlimited'
    ],
    state,
    {
      minOrderCost: Number(state.minOrderCost),
      tzOffset: Number(state.tzOffset),
      customer_order: Number(state.customer_order),
      zoneDeliveryCost: Number(state.zoneDeliveryCost),
      zoneSlotMaxWeight: state.zoneSlotMaxWeightUnlimited ? 0 : Number(state.zoneSlotMaxWeight) * 1000,
      zoneFirstDeliveryCost: Number(state.zoneFirstDeliveryCost),
      zoneExpressMaxWeight: state.zoneExpressMaxWeightUnlimited ? 0 : Number(state.zoneExpressMaxWeight) * 1000,
      zoneExpressDeliveryCost: Number(state.zoneExpressDeliveryCost),
      zoneTsExpressDeliveryStart: Number(state.zoneTsExpressDeliveryFinish),
      zoneTsExpressDeliveryFinish: Number(state.zoneTsExpressDeliveryFinish),
      expressDeliveryMaxShippingTime: Number(state.expressDeliveryMaxShippingTime),
      freeExpressDeliveryCostFrom: Number(state.freeExpressDeliveryCostFrom),
      freeSlotDeliveryCostFrom: Number(state.freeSlotDeliveryCostFrom),
      slotPriceMultuplier: Number(state.slotPriceMultuplier),
      slotPriceAdditional: Number(state.slotPriceAdditional),
      expressPriceMultuplier: Number(state.expressPriceMultuplier),
      expressPriceAdditional: Number(state.expressPriceAdditional),
      slotPlans: state.slotPlans.map((slotPlan) => {
        return {
          id: slotPlan.id,
          positionsLimit: Number(slotPlan.positionsLimit) || 0,
          ordersLimit: Number(slotPlan.ordersLimit) || 0,
          slotStartTime: slotPlan.slotStartTime,
          slotFinishTime: slotPlan.slotFinishTime,
          weekDay: Number(slotPlan.weekDay),
          slotTimeout: Number(slotPlan.slotTimeout) * 60000 || 0
        }
      }),
    }
  )

  return {
    ...prepared,
    deliveryProviders: [ ...state.slotDeliveryProviders, ...state.expressDeliveryProviders ]
  }
}